'use client';

import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Avatar, Flex, Text, useColorMode } from '@eqi-investimentos/react';
import { envConfig } from '@/config/env';
import { PATHS } from '@/config/paths';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { useGetSuggestedWallet } from '@/modules/Wallet/http/client/useGetSuggestedWallet';
import { useAuth } from '@/contexts/AuthContext';
import { Drawer } from '@/components/molecules/Drawer';
import { MY_PROFILE_DRAWER_ID, PERSONAL_INFO_DRAWER_ID } from './utils';
import { useGetAccount } from '../../http/client/useGetAccount';
import { ProfileItem } from './Item';
import { PersonalInfoDrawer } from './PersonalInfo';
import { SUPPORT_DRAWER_ID, SupportDrawer } from './Support';
export const ProfileDrawer = () => {
  const {
    t
  } = useTranslation('common');
  const {
    logout
  } = useAuth();
  const {
    data: walletInvestorProfile = {
      profile_title: '',
      categories: [],
      strategies: [],
      subcategories: []
    }
  } = useGetSuggestedWallet();
  const {
    toggleColorMode,
    colorMode
  } = useColorMode();
  const config = [{
    id: 'theme',
    leftIcon: 'Brightness',
    onChange: toggleColorMode,
    type: 'switch',
    value: colorMode === 'light'
  }, {
    id: 'profileData',
    leftIcon: 'PersonOutline',
    type: 'action',
    onClick: () => window.dispatchEvent(new CustomEvent(`open-chakra-modal-${PERSONAL_INFO_DRAWER_ID}`))
  }, {
    id: 'bookmark',
    leftIcon: 'BookmarkOutline',
    isExternal: false,
    path: PATHS.user.myList,
    type: 'navigation'
  }, {
    id: 'history',
    leftIcon: 'HistoryOutline',
    isExternal: false,
    path: PATHS.user.myHistoric,
    type: 'navigation'
  }, {
    id: 'termsOfUse',
    leftIcon: 'ListAltOutline',
    path: 'https://eqi.com.br/politicas',
    isExternal: true,
    type: 'navigation'
  }, {
    id: 'privacyPolicy',
    leftIcon: 'LockOutline',
    path: 'https://eqi.com.br/politicas',
    isExternal: true,
    type: 'navigation'
  }, {
    id: 'customerServices',
    leftIcon: 'ContactSupportOutline',
    type: 'action',
    onClick: () => window.dispatchEvent(new CustomEvent(`open-chakra-modal-${SUPPORT_DRAWER_ID}`))
  }] as const;
  const {
    data: user
  } = useGetAccount();
  const {
    data: accountStatus = {
      isEqi: false,
      accountService: 'Cliente Digital'
    }
  } = useGetCustomerStatus();
  const accountDetails = useMemo(() => {
    if (accountStatus.isEqi) return `Perfil ${walletInvestorProfile.profile_title} • ${accountStatus.accountService}`;
    return `Perfil ${walletInvestorProfile.profile_title}`;
  }, [accountStatus, walletInvestorProfile]);
  return <>
      <Drawer id={MY_PROFILE_DRAWER_ID} showIconButton headerContent={<Text size="2xl" color="content.primary">
            {t('drawers.profile.header')}
          </Text>} headerProps={{
      alignItems: 'center',
      justifyContent: 'center',
      pl: '4.25rem',
      pt: '3xl'
    }} closeButtonProps={{
      top: '5xl'
    }} bodyProps={{
      px: '2xl'
    }} backButtonProps={{
      buttonProps: {
        top: '3xl',
        left: '2xl'
      }
    }} data-sentry-element="Drawer" data-sentry-source-file="index.tsx">
        <Flex flexDir="column" gap="3xl" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <Flex w="full" alignItems="center" gap="s" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            <Avatar name={user?.name ?? 'Usuário'} showLabels={false} data-sentry-element="Avatar" data-sentry-source-file="index.tsx" />
            <Flex flexDir="column" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
              <Text size="xl" color="content.brand" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                {user?.name}
              </Text>
              <Text size="m" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
                {accountDetails}
              </Text>
            </Flex>
          </Flex>
          <Flex flexDir="column" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            {config.map(({
            id,
            ...props
          }) => <ProfileItem key={id} title={t(`drawers.profile.menu.${id}`)} {...props} />)}
            <ProfileItem ID="button_logout_profile_drawer" title={t('drawers.profile.logoutButton')} leftIcon="Logout" type="action" onClick={() => logout()} showArrow={false} pt="2.375rem" data-sentry-element="ProfileItem" data-sentry-source-file="index.tsx" />
          </Flex>
          <Flex flexDir="column" w="full" h="2rem" my="2rem" alignItems="center" justifyContent="center" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            <Text lineHeight="7" fontWeight="light" fontSize="sm" color="content.primary" data-sentry-element="Text" data-sentry-source-file="index.tsx">
              {`${t('drawers.profile.appVersion')} ${envConfig?.appVersion}`}
            </Text>
          </Flex>
        </Flex>
      </Drawer>
      <PersonalInfoDrawer data-sentry-element="PersonalInfoDrawer" data-sentry-source-file="index.tsx" />
      <SupportDrawer data-sentry-element="SupportDrawer" data-sentry-source-file="index.tsx" />
    </>;
};